import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CampaignWebhookService} from '../../service/api/campaign-webhook.service';
import {CampaignWebhookSubscriptionService} from '../../service/api/campaign-webhook-subscription.service';
import {CampaignWebhookDto} from '../../model/dto/entity/campaign-webhook.dto';
import {CampaignWebhookSubscriptionDto} from '../../model/dto/entity/campaign-webhook-subscription.dto';
import {WebhookDeliveryDto} from '../../model/dto/entity/webhook-delivery.dto';
import {WebhookDeliveryAlertDto} from '../../model/dto/entity/webhook-delivery-alert.dto';
import {TestWebhookResponse, WebhookDeliveryStats} from '../../model/dto/api/webhook.interface';

/**
 * Webhook Manager Component (F-033: Webhook and Event Notifications).
 * Provides UI for managing webhooks and their subscriptions for campaigns.
 *
 * Acceptance Criteria:
 * - Given webhook is configured, when campaign activates, then webhook is called with campaign data
 * - Given member status changes, when event is configured, then event is published to message queue
 * - Given webhook fails, when retry logic runs, then failures are logged and alerted
 */
@Component({
    selector: 'ct-webhook-manager',
    templateUrl: './webhook-manager.component.html',
    standalone: false
})
export class WebhookManagerComponent implements OnChanges {

    @Input() campaignId: number;
    @Input() campaignName: string;
    @Input() userId: number;

    @Output() webhookConfigured = new EventEmitter<CampaignWebhookSubscriptionDto>();
    @Output() webhookRemoved = new EventEmitter<number>();

    // Available webhooks
    availableWebhooks: CampaignWebhookDto[] = [];
    selectedWebhookId: number;

    // Subscriptions for this campaign
    subscriptions: CampaignWebhookSubscriptionDto[] = [];

    // Delivery history and stats
    deliveryHistory: WebhookDeliveryDto[] = [];
    deliveryStats: WebhookDeliveryStats | null = null;

    // Alerts
    alerts: WebhookDeliveryAlertDto[] = [];
    unacknowledgedAlertCount: number = 0;

    // UI state
    loading: boolean = false;
    testing: boolean = false;
    error: string = '';
    successMessage: string = '';
    activeTab: 'subscriptions' | 'webhooks' | 'history' | 'alerts' = 'subscriptions';

    // Event filter options
    eventFilterOptions = [
        {value: 'CAMPAIGN_ACTIVATED', label: 'Kampagne aktiviert'},
        {value: 'CAMPAIGN_PAUSED', label: 'Kampagne pausiert'},
        {value: 'CAMPAIGN_COMPLETED', label: 'Kampagne abgeschlossen'},
        {value: 'MEMBER_ADDED', label: 'Mitglied hinzugefügt'},
        {value: 'MEMBER_STATUS_CHANGED', label: 'Mitgliedsstatus geändert'},
        {value: 'MEMBER_CONVERTED', label: 'Mitglied konvertiert'},
        {value: 'MEMBER_REMOVED', label: 'Mitglied entfernt'},
        {value: 'REFRESH_COMPLETED', label: 'Aktualisierung abgeschlossen'},
        {value: 'REFRESH_FAILED', label: 'Aktualisierung fehlgeschlagen'}
    ];
    selectedEventFilters: string[] = [];

    constructor(
        private webhookService: CampaignWebhookService,
        private subscriptionService: CampaignWebhookSubscriptionService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['campaignId'] && this.campaignId) {
            this.loadData();
        }
        if (changes['userId'] && this.userId) {
            this.loadAlerts();
        }
    }

    loadData(): void {
        if (!this.campaignId) return;

        this.loading = true;
        this.error = '';

        this.loadAvailableWebhooks();
        this.loadSubscriptions();
    }

    loadAvailableWebhooks(): void {
        this.webhookService.getActiveWebhooks().subscribe({
            next: (webhooks) => {
                this.availableWebhooks = webhooks;
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to load webhooks', err);
                this.error = 'Verfügbare Webhooks konnten nicht geladen werden.';
                this.loading = false;
            }
        });
    }

    loadSubscriptions(): void {
        this.subscriptionService.getSubscriptionsByCampaign(this.campaignId).subscribe({
            next: (subs) => {
                this.subscriptions = subs;
            },
            error: (err) => {
                console.error('Failed to load subscriptions', err);
            }
        });
    }

    loadAlerts(): void {
        if (!this.userId) return;

        this.webhookService.getUnacknowledgedAlerts(this.userId).subscribe({
            next: (alerts) => {
                this.alerts = alerts;
                this.unacknowledgedAlertCount = alerts.length;
            },
            error: (err) => {
                console.error('Failed to load alerts', err);
            }
        });
    }

    /**
     * Subscribes the current campaign to a webhook.
     * F-033: Acceptance Criteria - "Given webhook is configured, when campaign activates,
     * then webhook is called with campaign data"
     */
    subscribeToWebhook(): void {
        if (!this.selectedWebhookId || !this.campaignId) {
            this.error = 'Bitte wählen Sie einen Webhook zum Abonnieren aus.';
            return;
        }

        const eventFiltersJson = this.selectedEventFilters.length > 0
            ? JSON.stringify({eventTypes: this.selectedEventFilters})
            : null;

        this.loading = true;
        this.webhookService.subscribeCampaign(this.selectedWebhookId, {
            campaignId: this.campaignId,
            eventFiltersJson: eventFiltersJson
        }).subscribe({
            next: (subscription) => {
                this.successMessage = 'Webhook erfolgreich abonniert.';
                this.loadSubscriptions();
                this.webhookConfigured.emit(subscription);
                this.selectedWebhookId = null;
                this.selectedEventFilters = [];
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to subscribe to webhook', err);
                this.error = 'Webhook konnte nicht abonniert werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Unsubscribes the current campaign from a webhook.
     */
    unsubscribeFromWebhook(webhookId: number): void {
        this.loading = true;
        this.webhookService.unsubscribeCampaign(webhookId, this.campaignId).subscribe({
            next: () => {
                this.successMessage = 'Webhook-Abonnement erfolgreich entfernt.';
                this.loadSubscriptions();
                this.webhookRemoved.emit(webhookId);
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to unsubscribe from webhook', err);
                this.error = 'Webhook-Abonnement konnte nicht entfernt werden.';
                this.loading = false;
            }
        });
    }

    /**
     * Tests a webhook by sending a test payload.
     */
    testWebhook(webhookId: number): void {
        this.testing = true;
        this.webhookService.testWebhook(webhookId).subscribe({
            next: (response: TestWebhookResponse) => {
                if (response.success) {
                    this.successMessage = 'Webhook-Test erfolgreich.';
                } else {
                    this.error = 'Webhook-Test fehlgeschlagen: ' + response.message;
                }
                this.testing = false;
            },
            error: (err) => {
                console.error('Failed to test webhook', err);
                this.error = 'Webhook konnte nicht getestet werden.';
                this.testing = false;
            }
        });
    }

    /**
     * Loads delivery history for a webhook.
     * F-033: Acceptance Criteria - "Given webhook fails, when retry logic runs,
     * then failures are logged and alerted"
     */
    loadDeliveryHistory(webhookId: number): void {
        this.loading = true;
        this.webhookService.getDeliveryHistory(webhookId).subscribe({
            next: (history) => {
                this.deliveryHistory = history;
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to load delivery history', err);
                this.loading = false;
            }
        });

        this.webhookService.getDeliveryStats(webhookId).subscribe({
            next: (stats) => {
                this.deliveryStats = stats;
            },
            error: (err) => {
                console.error('Failed to load delivery stats', err);
            }
        });
    }

    /**
     * Acknowledges a delivery alert.
     */
    acknowledgeAlert(alertId: number): void {
        this.webhookService.acknowledgeAlert(alertId).subscribe({
            next: () => {
                this.successMessage = 'Warnung bestätigt.';
                this.loadAlerts();
            },
            error: (err) => {
                console.error('Failed to acknowledge alert', err);
                this.error = 'Warnung konnte nicht bestätigt werden.';
            }
        });
    }

    /**
     * Clears messages after a timeout.
     */
    clearMessages(): void {
        this.error = '';
        this.successMessage = '';
    }

    /**
     * Gets the webhook name by ID.
     */
    getWebhookName(webhookId: number): string {
        const webhook = this.availableWebhooks.find(w => w.id === webhookId);
        return webhook ? webhook.name : 'Unbekannt';
    }

    /**
     * Gets delivery status color class.
     */
    getDeliveryStatusClass(status: string): string {
        switch (status) {
            case 'SUCCESS':
                return 'text-green-600';
            case 'FAILED':
            case 'EXHAUSTED':
                return 'text-red-600';
            case 'RETRYING':
                return 'text-yellow-600';
            case 'PENDING':
                return 'text-gray-600';
            default:
                return '';
        }
    }

    /**
     * Parses event filters from JSON string.
     */
    parseEventFilters(json: string): string[] {
        if (!json) return [];
        try {
            const parsed = JSON.parse(json);
            return parsed.eventTypes || [];
        } catch {
            return [];
        }
    }
}
